import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NgModel } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

import {
  ApiError,
  AuthService,
  BaseFilterCollapseService,
  ConfigService,
  MessageService,
} from '../../../../core';
import { TagsMultiSelectComponent } from '../../../../domains/shared/components/tags-multi-select/tags-multi-select.component';
import { EnumSituacao } from '../../../../domains/shared/enums/situacao.enum';
import { EnumCadastro } from '../../../../domains/shared/interfaces/cadastro.enum';
import { isModoCentralCompraCadastro } from '../../../../models/modo-central-compra';
import {
  Cidade,
  CidadeSearchesHttpService,
  CidadeSubRegiao,
  CidadeSubRegiaoBairro,
} from '../../../cidade';
import {
  ClienteGrupoEconomico,
  ClienteGrupoEconomicoCrudHttpService,
  ClienteGrupoEconomicoFilter,
  ClienteGrupoEconomicoSearchOrderBy,
  clienteGrupoEconomicoAtivo,
} from '../../../cliente-grupo-economico';
import { ConfigEmpresa } from '../../../configuracoes/app/empresa';
import { Estado, EstadoCrudHttpService } from '../../../estado';
import { RegiaoCidadeSubRegiaoHttpService } from '../../../regiao/cidade/sub-regiao/services';
import { ClienteAnaliseFinanceiraAuthGuardService } from '../analise-financeira/services';
import { Cliente } from '../cliente';
import { ClienteFilter } from './cliente-filter';
import { ClienteFilterStorageService } from './cliente-filter-storage.service';
import { PeriodicidadeCompraMultiSelectComponent } from 'app/domains/shared/components/periodicidade-compra-multi-select/periodicidade-compra-multi-select.component';
import { SemanaMultiSelectComponent } from 'app/domains/shared/components/semana-multi-select/semana-multi-select.component';

interface Config {
  canVisualizarStatusesAnaliseFinanceira: boolean;
  isModoCentralCompraCadastro: boolean;
}

@Component({
  selector: 'sgt-cliente-filter',
  templateUrl: 'cliente-filter.component.html',
  styleUrls: ['./cliente-filter.component.less'],
})
export class ClienteFilterComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  config: Config;
  configEmpresa: ConfigEmpresa;
  isRepresentante: boolean;

  // Filter
  @Input() filter: ClienteFilter;
  isFilterInitializing = false;
  isFilterInitialized = false;
  @Input() allowStorage = true;
  @Output() filterInitialized = new EventEmitter<any>();
  @Output() filterChange = new EventEmitter<any>();
  @Output() filterCleaned = new EventEmitter<any>();

  // Filrer pesquisa
  filterPsqSubscription: Subscription;
  isFirstChangeFilterPsq = true;
  @ViewChild('filterPsq', { static: true }) filterPsq: NgModel;

  // Filter collapse
  @Input() isCollapsedInDefault = false;
  isCollapsedIn = false;
  @ViewChild('filterCollapse') filterCollapse: ElementRef;
  collapseIcon = 'fas fa-caret-down';

  gruposEconomicos: ClienteGrupoEconomico[] = [];
  subRegioes: CidadeSubRegiao[];
  bairros: CidadeSubRegiaoBairro[];
  estados: Estado[] = [];

  /** Cadastro */
  @Input() somenteClientesEmCarteira: boolean = null; // Somente os clientes em carteira (clientes com cadastro igual a cliente)
  @Input() somenteClientesEmCarteiraAtiva: boolean = null; // Somente os clientes em carteira (clientes com cadastro igual a cliente) que estão com situação igual a Ativo
  @Input() somenteClientesEmCarteiraInativa: boolean = null; // Somente os clientes em carteira (clientes com cadastro igual a cliente) que estão com situação igual a Inativo
  @Input() somenteClientes: boolean = null; // Somente os clientes com cadastro igual a cliente
  @Input() somenteClientesProspects: boolean = null; // Somente os clientes com cadastro prospect
  @Input() somenteClientesCongelados: boolean = null; // Somente os clientes com cadastro congelado
  @Input() somenteClientesBloqueados: boolean = null; // Somente os clientes com cadastro bloqueado
  @Input() somenteClientesBaixados: boolean = null; // Somente os clientes com cadastro baixado
  @Input() somenteClientesOutros: boolean = null; // Somente os clientes com cadastro outros
  @Input() cadastroProspect = false;
  @Input() cadastroCliente = false;
  @Input() cadastroBaixado = false;
  @Input() cadastroOutros = false;

  /** Situação */
  @Input() somenteClientesAtivos: boolean = null; // Somente os clientes com a situação ativo
  @Input() somenteClientesInativos: boolean = null; // Somente os clientes com a situação inativo
  @Input() situacaoNovo = false;
  @Input() situacaoPositivo = false;
  @Input() situacaoAtivo = false;
  @Input() situacaoPreInativo = false;
  @Input() situacaoInativo = false;
  @Input() situacaoCongelado = false;
  @Input() situacaoBloqueado = false;
  @Input() situacaoPreBaixado = false;
  @Input() situacaoBaixado = false;
  @Input() situacaoOutros = false;

  // Multiselects
  isFirstChangeClassificacoesMultiselect = true;
  isFirstChangeRepresentanteMultiselect = true;
  isFirstChangePeriodicidadesComprasMultiselect = true;

  isCadastroItem: number;
  situacao: number;

  @ViewChild('tagsMultiSelect') tagsMultiSelect: TagsMultiSelectComponent;

  @ViewChild('periodicidadeCompraMultiSelectRef')
  periodicidadeCompraMultiSelectRef!: PeriodicidadeCompraMultiSelectComponent;

  @ViewChild('semanasMultiSelectRef')
  semanasMultiSelectRef!: SemanaMultiSelectComponent;

  constructor(
    private authService: AuthService,
    private baseFilterCollapseService: BaseFilterCollapseService,
    private configService: ConfigService,
    private messageService: MessageService,
    private clienteFilterStorageService: ClienteFilterStorageService,
    private clienteGrupoEconomicoCrudHttpService: ClienteGrupoEconomicoCrudHttpService,
    private cidadeSearchesHttpService: CidadeSearchesHttpService,
    private regiaoCidadeSubRegiaoHttpService: RegiaoCidadeSubRegiaoHttpService,
    private estadoCrudHttpService: EstadoCrudHttpService,
    private clienteAnaliseFinanceiraAuthGuardService: ClienteAnaliseFinanceiraAuthGuardService
  ) {}

  ngOnInit() {
    this.configEmpresa = this.configService.getEmpresa();
    this.isRepresentante = this.authService.isRepresentante();

    this.isCadastroItem = EnumCadastro.CLIENTE;
    this.situacao = EnumSituacao.ATIVO;

    // Setando config
    this.setConfig();
    // Aplicando o filtro salvo no storage
    if (this.allowStorage) {
      this.clienteFilterStorageService.applyFilter(this.filter);
    }
    // Iniciando filtro
    this.initFilter();
  }

  ngAfterViewInit() {
    if (!this.isCollapsedInDefault) {
      jQuery(this.filterCollapse.nativeElement).css('display', 'none');
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    for (const propName in changes) {
      if (propName === 'isCollapsedInDefault') {
        if (changes[propName].isFirstChange()) {
          setTimeout(() => {
            this.isCollapsedIn = changes[propName].currentValue;
          }, 100);
        }
      }
    }
  }

  private setConfig() {
    this.config = {
      canVisualizarStatusesAnaliseFinanceira:
        this.clienteAnaliseFinanceiraAuthGuardService.canVisualizar(),
      isModoCentralCompraCadastro: isModoCentralCompraCadastro(
        this.configEmpresa.modoCentralCompra
      ),
    };
  }

  /** Inicializa o filtro */
  private initFilter() {
    this.isFilterInitializing = true;
    this.isFilterInitialized = false;

    // Filter de pesquisa
    this.subscribeToFilterPsq();

    setTimeout(async () => {
      if (this.filter.idsPeriodicidadesCompras.length > 0) {
        this.periodicidadeCompraMultiSelectRef.setSelection(
          this.filter.idsPeriodicidadesCompras
        );
      }

      if (this.filter.periodicidadeCompraSemana.length > 0) {
        this.semanasMultiSelectRef.setSelection(
          this.filter.periodicidadeCompraSemana
        );
      }
    });

    Promise.all([this.searchGruposEconomicos(), this.searchEstados()])
      .then(() => {
        this.isFilterInitializing = false;
        this.isFilterInitialized = true;
        this.filterInitialized.emit();
      })
      .catch(() => {
        this.isFilterInitializing = false;
      });
  }

  // Realiza o subscribe nas alterações feitas no input do filtro da pesquisa
  private subscribeToFilterPsq() {
    this.filterPsqSubscription = this.filterPsq.valueChanges
      .pipe(
        debounceTime(1000), // Dando um debounce de 1000ms para emitir a alteração
        map((emittedValue) => emittedValue?.trim()),
        distinctUntilChanged()
      )
      .subscribe(() => {
        if (this.isFirstChangeFilterPsq) {
          this.isFirstChangeFilterPsq = false;
          return;
        }

        if (this.filter.filterPsq?.trim().length > 0) {
          this.filter.isFilterDirty = true;
        }

        this.filterChange.emit();
      });
  }

  onChangeTags(tags: number[]) {
    this.filter.idsTags = tags.length ? tags : null;
    this.filterChange.emit();
  }

  onChangePeriodicidadesCompras(idPeriodicidadesCompras: number[]) {
    this.filter.idsPeriodicidadesCompras = idPeriodicidadesCompras;

    // Como este filtro esta dentro do seletor avançado não emitimos evento após a seleção, sómente após o clique de busca do filtro.
    // this.filterChange.emit();
  }

  onChangePeriodicidadesComprasSemanas(idSemanas: number[]) {
    this.filter.periodicidadeCompraSemana = idSemanas;

    // Como este filtro esta dentro do seletor avançado não emitimos evento após a seleção, sómente após o clique de busca do filtro.
    // this.filterChange.emit();
  }

  /** Emite evento de alteração no filtro para atualizar a pesquisa */
  refresh() {
    setTimeout(() => {
      this.close();
      this.filterChange.emit();
    });
  }

  /** Limpa o filtro e atualiza a pesquisa */
  clear() {
    // Removendo os filtros
    this.baseFilterCollapseService.clear(this);

    this.close();
    this.tagsMultiSelect.clearSelection();
    this.periodicidadeCompraMultiSelectRef.clearSelection();
    this.semanasMultiSelectRef.clearSelection();
    this.filterCleaned.emit();
  }

  /** Abre/Fecha o filtro */
  toggleCollapse() {
    this.baseFilterCollapseService.toggleCollapse(this);
  }

  /** Abre o filtro */
  open() {
    this.baseFilterCollapseService.open(this);
  }

  /** Fecha o filtro */
  close() {
    this.baseFilterCollapseService.close(this);
  }

  /** Verifica se deve ocultar o cadastro no filtro */
  hideCadastro(): boolean {
    // Somente deve exibir o cadastro no filtro se não tiver nenhuma opção específica de filtro do cadastro informada
    return (
      this.somenteClientesEmCarteira ||
      this.somenteClientesEmCarteiraAtiva ||
      this.somenteClientesEmCarteiraInativa ||
      this.somenteClientesProspects ||
      this.somenteClientes ||
      this.somenteClientesCongelados ||
      this.somenteClientesBloqueados ||
      this.somenteClientesBaixados ||
      this.somenteClientesOutros ||
      this.cadastroProspect ||
      this.cadastroCliente ||
      this.cadastroBaixado ||
      this.cadastroOutros
    );
  }

  /** Ao alterar os cadastros */
  onChangeCadastros() {
    this.filterChange.emit();
  }

  /** Verifica se deve ocultar a situação no filtro */
  hideSituacao(): boolean {
    // Somente deve exibir a situação no filtro se não tiver as opções de carteira ativa ou inativa e nenhuma opção específica de filtro da situação informada
    return (
      this.somenteClientesEmCarteiraAtiva ||
      this.somenteClientesEmCarteiraInativa ||
      this.somenteClientesAtivos ||
      this.somenteClientesInativos ||
      this.situacaoNovo ||
      this.situacaoPositivo ||
      this.situacaoAtivo ||
      this.situacaoPreInativo ||
      this.situacaoInativo ||
      this.situacaoCongelado ||
      this.situacaoBloqueado ||
      this.situacaoPreBaixado ||
      this.situacaoBaixado ||
      this.situacaoOutros
    );
  }

  /** Ao alterar as situações */
  onChangeSituacoes() {
    this.filterChange.emit();
  }

  /** Ao alterar as classificações */
  onChangeClassificacoes() {
    if (this.isFirstChangeClassificacoesMultiselect) {
      this.isFirstChangeClassificacoesMultiselect = false;
      return;
    }

    this.filterChange.emit();
  }

  /** Ao alterar os representantes */
  onChangeRepresentantes() {
    if (this.isFirstChangeRepresentanteMultiselect) {
      this.isFirstChangeRepresentanteMultiselect = false;
      return;
    }

    // Pesquisando as sub-regiões das cidades dos representantes selecionados
    this.searchCidadeSubRegioes();

    this.filterChange.emit();
  }

  /** Ao alterar a data de cadastro inicial/final */
  onChangeDataCadastro(date: Date) {
    if (date) {
      this.filter.isFilterDirty = true;
    }
  }

  /** Ao alterar o cliente central de compras */
  onChangeClienteCentralCompras(clienteCentralCompras: Cliente) {
    if (clienteCentralCompras) {
      this.filter.idClienteCentralCompras = clienteCentralCompras.id;
      this.filter.isFilterDirty = true;
    } else {
      this.filter.idClienteCentralCompras = null;
    }
  }

  /** Ao alterar os statuses da análise financeira */
  onChangeStatusesAnaliseFinanceira() {
    this.filter.isFilterDirty = true;
  }

  /** Pesquisa os grupos dos clientes cadastrados */
  private searchGruposEconomicos() {
    const filter = new ClienteGrupoEconomicoFilter();
    // Somente Ativos
    filter.situacao = clienteGrupoEconomicoAtivo.id;
    // Representante
    if (this.isRepresentante) {
      // Representante logado
      filter.idsRepresentantes = [this.authService.getIdRepresentante()];
    }

    this.clienteGrupoEconomicoCrudHttpService
      .searchWithFilter(
        filter,
        ClienteGrupoEconomicoSearchOrderBy.Nome,
        'asc',
        null,
        null
      )
      .subscribe(
        (search) => (this.gruposEconomicos = search?.entities),
        (error: ApiError) =>
          this.messageService.error(
            error.message,
            'Erro ao pesquisar os grupos econômicos para o filtro',
            error
          )
      );
  }

  /** Pesquisa todos os estados */
  private searchEstados() {
    this.estadoCrudHttpService.search().subscribe(
      (entities) => (this.estados = entities),
      (error: ApiError) =>
        this.messageService.error(
          error.message,
          'Erro ao pesquisar os estados para o filtro do cliente',
          error
        )
    );
  }

  /** Ao alterar a cidade */
  onChangeCidade(cidade: Cidade) {
    if (cidade) {
      this.filter.idCidade = cidade.id;
      this.searchCidadeSubRegioes();
      this.filter.isFilterDirty = true;
    } else {
      this.filter.idCidade = null;
      this.clearCidadeSubRegioes();
    }
  }

  /** Pesquisa as sub-regiões da cidade informada e representante */
  private searchCidadeSubRegioes() {
    // this.subRegioes = [];
    // Obrigatório ter informado a cidade no filtro para pesquisar suas sub-regiões e bairros
    if (!this.filter?.idCidade) {
      this.clearCidadeSubRegioes();
      return;
    }

    // Representante
    let idsRepresentantes: number[] = [];
    if (this.isRepresentante) {
      idsRepresentantes = [this.authService.getIdRepresentante()];
    } else {
      idsRepresentantes = this.filter?.idsOutrosRepresentantes ?? [];
    }

    if (idsRepresentantes && idsRepresentantes.length > 0) {
      // Se o representante estiver informado no filtro, então pesquisamos somente as sub-regiões e bairros disponíveis para ele na cidade
      this.regiaoCidadeSubRegiaoHttpService
        .searchDisponiveisParaRepresentantes(
          idsRepresentantes,
          this.filter?.idCidade
        )
        .subscribe(
          (entities) => {
            this.subRegioes = entities;
            this.searchCidadeSubRegiaoBairros(this.filter.idCidadeSubRegiao);
          },
          (error: ApiError) =>
            this.messageService.error(
              error.message,
              'Erro ao pesquisar as sub-regiões e bairros da cidade',
              error
            )
        );
    } else {
      // Se não, pesquisamos todas as sub-regiões e bairros da cidade
      this.cidadeSearchesHttpService
        .searchSubRegioes(this.filter?.idCidade, true)
        .subscribe(
          (entities) => {
            this.subRegioes = entities;
            this.searchCidadeSubRegiaoBairros(this.filter.idCidadeSubRegiao);
          },
          (error: ApiError) =>
            this.messageService.error(
              error.message,
              'Erro ao pesquisar as sub-regiões e bairros da cidade',
              error
            )
        );
    }
  }

  /** Ao alterar a sub-região no select */
  onChangeCidadeSubRegiao(id: number) {
    this.filter.idCidadeSubRegiaoBairro = null;
    this.searchCidadeSubRegiaoBairros(id);
  }

  /** Limpa as sub-regiões e seus bairros da cidade */
  private clearCidadeSubRegioes() {
    // Cidade Sub-Regiões
    this.subRegioes = [];
    this.filter.idCidadeSubRegiao = null;
    // Cidade Sub-Regiões Bairros
    this.bairros = [];
    this.filter.idCidadeSubRegiaoBairro = null;
  }

  /** Pesquisa os bairros da sub-região informada */
  private searchCidadeSubRegiaoBairros(idCidadeSubRegiao: number) {
    this.bairros = [];
    if (!this.subRegioes) {
      return;
    }

    const subRegiaoAtual = this.subRegioes.find(
      (subRegiao) => subRegiao.id === idCidadeSubRegiao
    );
    if (subRegiaoAtual) {
      this.bairros = subRegiaoAtual.bairros;
    }
  }

  ngOnDestroy() {
    this.filterPsqSubscription?.unsubscribe();
  }
}
